import { useCallback, useMemo, useState } from "react"
import { toast } from "react-toastify"
import { Badge, Button, Col, Row, UncontrolledTooltip } from "reactstrap"

import { CanView } from "components/Common/CanView"
import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import PageWrapper from "components/Common/PageWrapper"
import TableContainer from "components/Common/TableContainer"
import { ACCESS } from "constants/accessPermissions"
import {
  useDeleteEmployeeDesignation,
  useEmployeeDesignations,
} from "hooks/useEmployeeDesignation"
import { useListParams } from "hooks/useListParams"
import { formatReadableDate } from "utils/formatDate"

import EmployeeDesignationAddEdit from "./EmployeeDesignationAddEdit"

const EmployeeDesignationList = () => {
  document.title = "Employee Designations"

  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [selected, setSelected] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)

  const { params, setPage, setSearch } = useListParams({ page: 1, search: "" })

  const queryParams = useMemo(
    () => ({
      page: params.page ?? 1,
      per_page: 10,
      ...(params.search ? { search: params.search } : {}),
    }),
    [params]
  )

  const {
    data: response,
    isLoading,
    isRefetching,
    refetch,
  } = useEmployeeDesignations(queryParams)

  const { mutateAsync: removeDesignation, isPending: deleteLoading } =
    useDeleteEmployeeDesignation()

  const toggleModal = useCallback(() => {
    if (modal) {
      setModal(false)
      setSelected(null)
      setIsEdit(false)
    } else {
      setModal(true)
    }
  }, [modal])

  const handleAddNew = useCallback(() => {
    setSelected(null)
    setIsEdit(false)
    setModal(true)
  }, [])

  const handleEdit = useCallback(item => {
    setSelected(item)
    setIsEdit(true)
    setModal(true)
  }, [])

  const handleDeleteClick = useCallback(item => {
    setSelected(item)
    setDeleteModal(true)
  }, [])

  const handleDelete = useCallback(async () => {
    if (!selected?.id) return
    try {
      const res = await removeDesignation(selected.id)
      toast.success(res?.message ?? "Designation deleted successfully.")
      setDeleteModal(false)
      refetch()
    } catch (error) {
      console.error(error)
      toast.error(
        error?.response?.data?.message ?? "Failed to delete designation."
      )
    }
  }, [selected, removeDesignation, refetch])

  const columns = useMemo(
    () => [
      {
        header: "Title",
        accessorKey: "title",
        cell: ({ row }) => (
          <div className="fw-semibold">{row.original.title || "—"}</div>
        ),
      },
      {
        header: "Slug",
        accessorKey: "slug",
        cell: ({ row }) => (
          <Badge color="light" className="text-dark">
            {row.original.slug || "—"}
          </Badge>
        ),
      },
      {
        header: "Description",
        cell: ({ row }) => (
          <div style={{ wordBreak: "break-word", whiteSpace: "normal" }}>
            {row.original.description || "—"}
          </div>
        ),
      },
      {
        header: "Created",
        cell: ({ row }) =>
          row.original.created_at
            ? formatReadableDate(row.original.created_at)
            : "—",
      },
      {
        header: "Action",
        cell: ({ row }) => {
          const item = row.original
          return (
            <ul className="list-unstyled hstack gap-1 mb-0">
              <CanView access={[ACCESS.EMPLOYEE_DESIGNATION.EDIT]}>
                <li>
                  <Button
                    color="info"
                    size="sm"
                    onClick={() => handleEdit(item)}
                    id={`edit-desig-${item.id}`}
                  >
                    <i className="mdi mdi-pencil-outline" />
                  </Button>
                  <UncontrolledTooltip target={`edit-desig-${item.id}`}>
                    Edit
                  </UncontrolledTooltip>
                </li>
              </CanView>
              <CanView access={[ACCESS.EMPLOYEE_DESIGNATION.DELETE]}>
                <li>
                  <Button
                    color="danger"
                    size="sm"
                    onClick={() => handleDeleteClick(item)}
                    id={`del-desig-${item.id}`}
                  >
                    <i className="mdi mdi-delete-outline" />
                  </Button>
                  <UncontrolledTooltip target={`del-desig-${item.id}`}>
                    Delete
                  </UncontrolledTooltip>
                </li>
              </CanView>
            </ul>
          )
        },
      },
    ],
    [handleEdit, handleDeleteClick]
  )

  const items = response?.data?.data ?? []
  const paginationInfo = response?.data ?? null

  return (
    <PageWrapper
      title="Employee Designations"
      breadcrumbItem="Employee Designations"
    >
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        loading={deleteLoading}
        onCloseClick={() => setDeleteModal(false)}
      />

      <Row className="mb-3 align-items-center">
        <Col sm={6}>
          <h5 className="mb-0">Employee Designations</h5>
        </Col>
        <Col sm={6} className="text-end">
          <CanView access={[ACCESS.EMPLOYEE_DESIGNATION.ADD]}>
            <Button color="primary" onClick={handleAddNew} className="me-1">
              <i className="bx bx-plus" /> Add Designation
            </Button>
          </CanView>
          <Button color="light" onClick={() => refetch()} id="refresh-desig">
            <i
              className={`mdi mdi-refresh ${isRefetching ? "mdi-spin" : ""}`}
            />
          </Button>
          <UncontrolledTooltip target="refresh-desig">
            Refresh
          </UncontrolledTooltip>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col sm={4}>
          <GlobalInputSearch value={params.search} onChange={setSearch} />
        </Col>
      </Row>

      <TableContainer
        columns={columns}
        data={items}
        isPagination={true}
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading || isRefetching}
        tableClass="align-middle table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline mt-2 border-top"
      />

      <EmployeeDesignationAddEdit
        modal={modal}
        toggleModal={toggleModal}
        isEdit={isEdit}
        editedData={selected}
      />
    </PageWrapper>
  )
}

export default EmployeeDesignationList
